import React, { useState, useEffect } from 'react';
import './App.css';
import {
  getTransactions,
  getSummary,
  addTransaction,
  deleteTransaction,
  updateTransaction,
  getAccounts,
  addAccount,
} from './api';

const EMPTY_FORM = {
  date: new Date().toISOString().split('T')[0],
  description: '',
  amount: '',
  type: 'spend',
  account_id: '1',
  category_id: '1',
  transfer_to_id: '',
  charge_amount: '',
};

const EMPTY_FILTERS = {
  startDate: '',
  endDate: '',
  description: '',
  type: '',
  accountId: '',
};

const EMPTY_ACCOUNT = { name: '', min_balance: '' };

const TYPE_LABEL = { earn: 'আয়', spend: 'ব্যয়', transfer: 'ট্রান্সফার' };

function App() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState(null); // null = notun entry mode, othoba edit hocche emon id
  const [theme, setTheme] = useState(() => localStorage.getItem('ledger-theme') || 'light');

  // ফিল্টার বার — যা টাইপ করা হচ্ছে (draft) বনাম যা আসলে অ্যাপ্লাই হয়েছে
  const [filterDraft, setFilterDraft] = useState({ ...EMPTY_FILTERS });
  const [activeFilters, setActiveFilters] = useState({ ...EMPTY_FILTERS });

  // নতুন অ্যাকাউন্ট যোগ করার প্যানেল
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [newAccount, setNewAccount] = useState({ ...EMPTY_ACCOUNT });
  const [accountError, setAccountError] = useState('');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ledger-theme', theme);
  }, [theme]);

  const loadAccounts = async () => {
    const acc = await getAccounts();
    setAccounts(acc);
  };

  const loadTransactions = async (filters) => {
    const txn = await getTransactions(filters);
    setTransactions(txn);
  };

  const loadData = async (filters = activeFilters) => {
    const [txn, summ] = await Promise.all([getTransactions(filters), getSummary()]);
    setTransactions(txn);
    setSummary(summ);
  };

  useEffect(() => {
    loadAccounts();
    loadData({ ...EMPTY_FILTERS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      // এডিট মোড — শুধু এই একটা row-ই আপডেট হবে (transfer হলে অন্য পাশ/ফি আলাদা row, সেগুলো বদলাবে না)
      await updateTransaction(editingId, form);
      setEditingId(null);
    } else {
      await addTransaction(form);
    }
    loadData();
    setForm({ ...EMPTY_FORM });
  };

  const handleEditClick = (txn) => {
    setEditingId(txn.id);
    setForm({
      date: txn.date,
      description: txn.description || '',
      amount: txn.amount,
      type: txn.type,
      account_id: String(txn.account_id),
      category_id: String(txn.category_id),
      transfer_to_id: '',
      charge_amount: '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  };

  const handleDeleteClick = async (txn) => {
    const ok = window.confirm(`"${txn.description}" (৳${txn.amount}) এই লেনদেনটি ডিলিট করতে চাও?`);
    if (!ok) return;
    await deleteTransaction(txn.id);
    loadData();
  };

  // ---------- ফিল্টার ----------
  const handleFilterField = (key, value) => {
    setFilterDraft((f) => ({ ...f, [key]: value }));
  };

  const applyFilters = async (e) => {
    if (e) e.preventDefault();
    setActiveFilters(filterDraft);
    await loadTransactions(filterDraft);
  };

  const clearFilters = async () => {
    setFilterDraft({ ...EMPTY_FILTERS });
    setActiveFilters({ ...EMPTY_FILTERS });
    await loadTransactions({ ...EMPTY_FILTERS });
  };

  const hasActiveFilters = Object.values(activeFilters).some((v) => v);

  // ফিল্টার করা লেনদেনের ভিত্তিতে স্টেটমেন্ট সারাংশ (প্রিন্টের জন্য)
  const filteredEarn = transactions.reduce((s, t) => s + (t.type === 'earn' ? t.amount : 0), 0);
  const filteredSpend = transactions.reduce((s, t) => s + (t.type === 'spend' ? t.amount : 0), 0);
  const filteredNet = filteredEarn - filteredSpend;

  const filterAccountName = activeFilters.accountId
    ? accounts.find((a) => String(a.id) === String(activeFilters.accountId))?.name
    : null;

  const statementRangeLabel = () => {
    if (activeFilters.startDate && activeFilters.endDate) return `${activeFilters.startDate} থেকে ${activeFilters.endDate}`;
    if (activeFilters.startDate) return `${activeFilters.startDate} থেকে এখন পর্যন্ত`;
    if (activeFilters.endDate) return `শুরু থেকে ${activeFilters.endDate} পর্যন্ত`;
    return 'সব তারিখ';
  };

  const handlePrint = () => {
    window.print();
  };

  // ---------- নতুন অ্যাকাউন্ট ----------
  const handleAddAccount = async (e) => {
    e.preventDefault();
    setAccountError('');
    if (!newAccount.name.trim()) {
      setAccountError('অ্যাকাউন্টের নাম দিন');
      return;
    }
    const res = await addAccount({ name: newAccount.name.trim(), min_balance: newAccount.min_balance || 0 });
    if (res.success) {
      setNewAccount({ ...EMPTY_ACCOUNT });
      setShowAccountForm(false);
      await Promise.all([loadAccounts(), loadData()]);
    } else {
      setAccountError(res.message || 'অ্যাকাউন্ট যোগ করা যায়নি');
    }
  };

  return (
    <div className="ledger">
      <header className="ledger-header">
        <div className="ledger-title">
          <img src="/logo192.png" alt="TAMIM হিসাব" className="app-logo" />
          <div>
            <h1>TAMIM হিসাব</h1>
            <span>আপনার হিসাব, আপনার নিয়ন্ত্রণ</span>
          </div>
        </div>
        <button
          type="button"
          className="theme-toggle"
          onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
        >
          {theme === 'light' ? '🌙 ডার্ক মোড' : '☀️ লাইট মোড'}
        </button>
      </header>

      <div className="account-grid">
        {summary.map((acc) => {
          const earn = acc.total_earn || 0;
          const spend = acc.total_spend || 0;
          const balance = earn - spend;
          const withdrawable = balance - (acc.min_balance || 0);
          const total = earn + spend;
          const earnPct = total > 0 ? Math.round((earn / total) * 100) : 0;
          return (
            <div key={acc.account_id || acc.account_name} className={`account-card ${balance < 0 ? 'is-negative' : 'is-positive'}`}>
              <h3>{acc.account_name}</h3>
              <div className="account-row earn"><span>আয়</span><span className="num">৳{earn.toFixed(2)}</span></div>
              <div className="account-row spend"><span>ব্যয়</span><span className="num">৳{spend.toFixed(2)}</span></div>
              <div className="account-bar">
                <div className="account-bar-fill" style={{ width: `${earnPct}%` }} />
              </div>
              <div className="account-balance">
                <span className="label">ব্যালেন্স</span>
                <span className="num">৳{balance.toFixed(2)}</span>
              </div>
              <div className="account-withdrawable">উত্তোলনযোগ্য: ৳{withdrawable.toFixed(2)}</div>
            </div>
          );
        })}

        <div className="account-card account-add-card">
          {!showAccountForm ? (
            <button type="button" className="btn-add-account" onClick={() => setShowAccountForm(true)}>
              + নতুন অ্যাকাউন্ট যোগ করুন
            </button>
          ) : (
            <form onSubmit={handleAddAccount} className="new-account-form">
              <label>অ্যাকাউন্টের নাম</label>
              <input
                type="text"
                placeholder="যেমন: ব্র্যাক ব্যাংক"
                value={newAccount.name}
                onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                autoFocus
              />
              <label>সর্বনিম্ন ব্যালেন্স (ঐচ্ছিক)</label>
              <input
                type="number"
                placeholder="০"
                value={newAccount.min_balance}
                onChange={(e) => setNewAccount({ ...newAccount, min_balance: e.target.value })}
              />
              {accountError && <div className="account-error">{accountError}</div>}
              <div className="new-account-actions">
                <button type="submit" className="btn btn-primary">যোগ করুন</button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { setShowAccountForm(false); setNewAccount({ ...EMPTY_ACCOUNT }); setAccountError(''); }}
                >
                  বাতিল
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {editingId && (
        <div className="edit-banner">✏️ এডিট মোড — আইডি #{editingId} সম্পাদনা হচ্ছে</div>
      )}

      <form onSubmit={handleSubmit} className="ledger-form">
        <div className="field date">
          <label>তারিখ</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
        </div>
        <div className="field desc">
          <label>বিবরণ</label>
          <input type="text" placeholder="যেমন: বাজার" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
        </div>
        <div className="field amount">
          <label>টাকা</label>
          <input type="number" placeholder="০" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
        </div>
        <div className="field type">
          <label>ধরন</label>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="spend">ব্যয়</option>
            <option value="earn">আয়</option>
            {!editingId && <option value="transfer">ট্রান্সফার</option>}
          </select>
        </div>
        <div className="field account">
          <label>অ্যাকাউন্ট</label>
          <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>
        </div>
        {form.type === 'transfer' && !editingId && (
          <>
            <div className="field transfer-to">
              <label>ট্রান্সফার (যে অ্যাকাউন্টে)</label>
              <select value={form.transfer_to_id} onChange={(e) => setForm({ ...form, transfer_to_id: e.target.value })}>
                {accounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </div>
            <div className="field fee">
              <label>ফি (৳)</label>
              <input type="number" placeholder="যেমন: ১০" value={form.charge_amount} onChange={(e) => setForm({ ...form, charge_amount: e.target.value })} />
            </div>
          </>
        )}
        <button type="submit" className={`btn ${editingId ? 'btn-editing' : 'btn-primary'}`}>
          {editingId ? 'আপডেট করুন' : 'যোগ করুন'}
        </button>
        {editingId && (
          <button type="button" onClick={handleCancelEdit} className="btn btn-ghost">বাতিল</button>
        )}
      </form>

      {/* ---------- ফিল্টার বার (এক্সেলের মতো: তারিখ, বিবরণ, ধরন, অ্যাকাউন্ট) ---------- */}
      <form onSubmit={applyFilters} className="filter-bar">
        <div className="field date">
          <label>তারিখ থেকে</label>
          <input type="date" value={filterDraft.startDate} onChange={(e) => handleFilterField('startDate', e.target.value)} />
        </div>
        <div className="field date">
          <label>তারিখ পর্যন্ত</label>
          <input type="date" value={filterDraft.endDate} onChange={(e) => handleFilterField('endDate', e.target.value)} />
        </div>
        <div className="field desc">
          <label>বিবরণ খুঁজুন</label>
          <input type="text" placeholder="যেমন: বাজার" value={filterDraft.description} onChange={(e) => handleFilterField('description', e.target.value)} />
        </div>
        <div className="field type">
          <label>ধরন</label>
          <select value={filterDraft.type} onChange={(e) => handleFilterField('type', e.target.value)}>
            <option value="">সব</option>
            <option value="earn">আয়</option>
            <option value="spend">ব্যয়</option>
            <option value="transfer">ট্রান্সফার</option>
          </select>
        </div>
        <div className="field account">
          <label>অ্যাকাউন্ট</label>
          <select value={filterDraft.accountId} onChange={(e) => handleFilterField('accountId', e.target.value)}>
            <option value="">সব অ্যাকাউন্ট</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn btn-primary">ফিল্টার করুন</button>
        {hasActiveFilters && (
          <button type="button" onClick={clearFilters} className="btn btn-ghost">ফিল্টার মুছুন</button>
        )}
        <button type="button" onClick={handlePrint} className="btn btn-print">🖨️ প্রিন্ট / PDF</button>
      </form>

      {/* ---------- প্রিন্ট-এ শুধু এই অংশ দেখা যাবে (স্টেটমেন্ট হেডার) ---------- */}
      <div className="print-statement-header">
        <h2>TAMIM হিসাব — স্টেটমেন্ট</h2>
        <div className="print-meta">
          <span><strong>সময়কাল:</strong> {statementRangeLabel()}</span>
          <span><strong>অ্যাকাউন্ট:</strong> {filterAccountName || 'সব অ্যাকাউন্ট'}</span>
          {activeFilters.description && <span><strong>বিবরণ খুঁজা হয়েছে:</strong> {activeFilters.description}</span>}
          {activeFilters.type && <span><strong>ধরন:</strong> {TYPE_LABEL[activeFilters.type]}</span>}
          <span><strong>প্রিন্টের তারিখ:</strong> {new Date().toISOString().split('T')[0]}</span>
        </div>
        <div className="print-summary">
          <span>মোট আয়: ৳{filteredEarn.toFixed(2)}</span>
          <span>মোট ব্যয়: ৳{filteredSpend.toFixed(2)}</span>
          <span>নীট: ৳{filteredNet.toFixed(2)}</span>
          <span>মোট লেনদেন: {transactions.length}টি</span>
        </div>
      </div>

      <div className="ledger-table-wrap">
        <table className="ledger-table">
          <thead>
            <tr>
              <th>তারিখ</th>
              <th>বিবরণ</th>
              <th>টাকা</th>
              <th>ধরন</th>
              <th>অ্যাকাউন্ট</th>
              <th className="no-print">অ্যাকশন</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length === 0 && (
              <tr>
                <td colSpan="6" className="empty-row">এই ফিল্টারে কোনো লেনদেন পাওয়া যায়নি</td>
              </tr>
            )}
            {transactions.map((txn) => (
              <tr key={txn.id} className={editingId === txn.id ? 'is-editing' : ''}>
                <td>{txn.date}</td>
                <td>{txn.description}</td>
                <td className={`amount-cell ${txn.type === 'earn' ? 'earn' : 'spend'}`}>৳{txn.amount}</td>
                <td><span className={`type-chip ${txn.type}`}>{TYPE_LABEL[txn.type] || txn.type}</span></td>
                <td>{txn.account_name}</td>
                <td className="no-print">
                  <div className="row-actions">
                    <button className="icon-btn edit" onClick={() => handleEditClick(txn)}>এডিট</button>
                    <button className="icon-btn delete" onClick={() => handleDeleteClick(txn)}>ডিলিট</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default App;
